import React from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import styles from './stepper.module.scss';

// `variant` controls the visual style:
//   - 'default'  → legacy icon-based stepper (NOM/Win flows)
//   - 'numbered' → modern minimal stepper with numbers + checkmarks,
//                  single brand color throughout (DC flow, 2025 style)
function Stepper({ steps, activeStep, onStepClick, variant = 'default', maxStep }) {
  // Any step the user has already reached stays navigable, not just the ones
  // behind the current step — so from the last step you can jump anywhere,
  // and after editing an earlier step you can jump forward again to a step
  // you already completed. Falls back to activeStep (original behaviour)
  // when the parent doesn't track the furthest-reached step.
  const furthest = typeof maxStep === 'number' ? Math.max(maxStep, activeStep) : activeStep;
  const isCompleted = index => index !== activeStep && index <= furthest;
  const isClickable = index => isCompleted(index) && !!onStepClick;
  const isNumbered = variant === 'numbered';

  const handleStepClick = index => {
    if (isClickable(index)) {
      onStepClick(index);
    }
  };

  const handleStepKeyDown = (e, index) => {
    if (!isClickable(index)) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onStepClick(index);
    }
  };

  const containerClass = `${styles.stepperContainer} ${isNumbered ? styles.numbered : ''}`;

  return (
    <ol className={containerClass} aria-label="Campaign creation steps">
      {steps.map((stepMeta, index) => {
        const isActive = index === activeStep;
        const isDone = isCompleted(index);
        const canClick = isClickable(index);
        let stateLabel = 'upcoming';
        if (isActive) stateLabel = 'current step';
        else if (isDone) stateLabel = 'completed';

        let stateClass = styles.upcoming;
        if (isActive) stateClass = styles.active;
        else if (isDone) stateClass = styles.completed;

        // Render the step body — when a step is clickable (completed),
        // use an actual <button> so a11y/interaction semantics are
        // intrinsic; otherwise use a plain div (no click target).
        let icon;
        if (isNumbered && isDone) {
          icon = (
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3.5 8.5L6.5 11.5L12.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        } else if (isNumbered) {
          icon = <span className={styles.stepNumber}>{index + 1}</span>;
        } else {
          icon = <Image src={stepMeta.icon} alt="" width={28} height={28} aria-hidden="true" />;
        }

        const stepBody = (
          <>
            <div className={styles.stepLabel}>{stepMeta.label}</div>
            <div className={styles.stepIcon}>{icon}</div>
          </>
        );

        const stepAriaLabel = `${stepMeta.label} — ${stateLabel}`;

        // Keep the DOM shape consistent: the <li> ALWAYS carries
        // .step (so CSS selectors like `.step:not(:first-child)::before`
        // — which draws the connector line in the numbered variant —
        // see a uniform shape regardless of click state). The inner
        // element (<button> when clickable, <div> when not) only
        // handles interaction semantics.
        const InnerTag = canClick ? 'button' : 'div';
        const innerProps = canClick
          ? {
              type: 'button',
              onClick: () => handleStepClick(index),
              onKeyDown: e => handleStepKeyDown(e, index),
              'aria-label': stepAriaLabel,
            }
          : { 'aria-label': stepAriaLabel };

        return (
          <React.Fragment key={stepMeta.label}>
            <li
              className={`${styles.step} ${stateClass}`}
              aria-current={isActive ? 'step' : undefined}
            >
              <InnerTag className={styles.stepInner} {...innerProps}>
                {stepBody}
              </InnerTag>
            </li>
            {index < steps.length - 1 && (
              <div
                className={`${styles.stepperConnector} ${isDone ? styles.completed : ''}`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      iconKey: PropTypes.string,
    }),
  ).isRequired,
  activeStep: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'numbered']),
  // Furthest step index the user has reached; steps up to it stay clickable.
  maxStep: PropTypes.number,
};

export default Stepper;
